/**
 * Horizontal row of cards (APP3): touch scroll with snap points, 2 cards plus a peek of the
 * third on phones, more from `sm`. The row is a named region that takes keyboard focus;
 * ArrowLeft / ArrowRight scroll it by one card, and Tab reaches every card's own controls.
 */
import { Children, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@/utils/cn';

interface HorizontalCarouselProps {
  /** Accessible name of the row, e.g. the category name. */
  label: string;
  children: ReactNode;
  className?: string;
}

/** Matches `gap-3`; read from the computed style when the browser has one. */
const FALLBACK_GAP_PX = 12;

function scrollStep(list: HTMLElement): number {
  const item = list.firstElementChild;
  if (!item) return 0;
  const gap = Number.parseFloat(getComputedStyle(list).columnGap) || FALLBACK_GAP_PX;
  return item.getBoundingClientRect().width + gap;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

export function HorizontalCarousel({ label, children, className }: HorizontalCarouselProps) {
  const listRef = useRef<HTMLUListElement>(null);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const list = listRef.current;
    if (!list || typeof list.scrollBy !== 'function') return;
    event.preventDefault();
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    list.scrollBy({ left: direction * scrollStep(list), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  return (
    <div
      role="region"
      aria-label={label}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className={cn(
        'rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        className,
      )}
    >
      {/* tabIndex -1: Firefox would otherwise add the scroller itself as a second tab stop. */}
      <ul
        ref={listRef}
        tabIndex={-1}
        className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain scroll-px-4 px-4 pb-2 [scrollbar-width:thin] sm:mx-0 sm:scroll-px-0 sm:px-0"
      >
        {Children.map(children, (child) => (
          <li className="w-[42%] shrink-0 snap-start sm:w-[30%] md:w-[23%] lg:w-[18%]">{child}</li>
        ))}
      </ul>
    </div>
  );
}
